import { Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";

// Privacy Policy — Sprint 2 Batch 1 (May 2026).
// Pure static page. NO backend, NO database, NO collection of user data on this page.
// All text below is a reasonable starter for a SaaS that handles US tax documents
// for childcare centers. Review with counsel before broad public launch.
export default function Privacy() {
  const effectiveDate = "May 21, 2026";
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link
          to="/"
          data-testid="back-home-link"
          className="inline-flex items-center gap-2 text-sm text-[#2563EB] hover:text-blue-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#2563EB]" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Privacy Policy</h1>
              <p className="text-xs text-slate-500 mt-1">Effective {effectiveDate}</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none text-sm leading-relaxed space-y-6" data-testid="privacy-content">
            <p>
              Today's Kidz Simple Sign In Tax ("Simple Sign In Tax", "we", "us") is operated by
              Today's Kidz Childcare Center. We help licensed US childcare providers organize their
              tax documents, track CACFP (Child and Adult Care Food Program) reimbursements, and
              produce TurboTax-ready reports. This Privacy Policy explains what we collect, why we
              collect it, and what rights you have over your data.
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">1. Who this policy applies to</h2>
            <p>
              This policy applies to anyone who creates an account, uploads documents, or pays for
              a subscription on <strong>todayskidztax.com</strong>. If you are visiting the public
              landing page only and have not registered, the only data we may collect about you is
              standard server access logs (IP address, browser type, request time) for security
              and abuse prevention.
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">2. What we collect</h2>
            <ul className="list-disc list-outside ml-6 space-y-2">
              <li><strong>Account information:</strong> your name, email address, center name, US state, entity type (LLC / sole proprietor / etc.), and whether you participate in CACFP.</li>
              <li><strong>Tax documents:</strong> the bank statements, receipts, payroll records, CACFP reports, vehicle logs, and equipment receipts you upload. These contain personally identifiable financial information by their nature.</li>
              <li><strong>Generated tax data:</strong> the line-item transactions extracted from your uploads, your CACFP allocation percentages, your Schedule C summaries, your equipment depreciation schedules.</li>
              <li><strong>Payment information:</strong> we use Square as our payment processor. Card numbers are entered directly into Square's hosted checkout and are never stored on our servers. We retain only Square's payment confirmation IDs, the amount paid, and which tax year was activated.</li>
              <li><strong>Usage data:</strong> pages uploaded, number of transactions processed, login timestamps, IP addresses, and coarse geolocation derived from IP (state / country level only). This is used for trial-abuse prevention, security alerts, and aggregate metrics.</li>
              <li><strong>Email communication:</strong> we use Resend to send transactional emails (verification codes, password resets, renewal reminders, accountant exports). We retain a log of when each email was sent and to what address.</li>
            </ul>

            <h2 className="text-lg font-bold text-slate-900 mt-6">3. Why we collect it</h2>
            <ul className="list-disc list-outside ml-6 space-y-2">
              <li>To provide the core service: categorizing your tax documents and producing reports.</li>
              <li>To enforce paid subscriptions and prevent abuse of the free trial (one trial per email per filing year).</li>
              <li>To send you the notifications you've signed up for: renewal reminders, year-end checklist nudges, password reset codes.</li>
              <li>To detect suspicious account behavior (e.g., a single account being accessed from many countries within hours).</li>
              <li>To comply with US tax law record-retention guidance (we retain your tax-year data for 7 years from filing — the IRS audit window).</li>
            </ul>

            <h2 className="text-lg font-bold text-slate-900 mt-6">4. Who we share with (third parties)</h2>
            <p>
              We use a small number of third-party services to run the app. Each is a contractual
              data-processor — they handle data on our behalf and do not own it. They are:
            </p>
            <ul className="list-disc list-outside ml-6 space-y-2">
              <li><strong>Square</strong> — payment processing. Receives only billing email, transaction amount, and Square's internal IDs.</li>
              <li><strong>Resend</strong> — transactional email delivery. Receives your email address and the message body.</li>
              <li><strong>Cloudflare R2</strong> — encrypted object storage for your uploaded source files (bank statements, receipts). Files are not indexed or read by Cloudflare; they sit in a bucket only our application server can access.</li>
              <li><strong>Google Gemini (via Emergent)</strong> — used to extract text from uploaded PDFs and images so we can categorize transactions. Per Google's Gemini API terms, your file contents are NOT used to train any model. CSV and Excel files do NOT pass through Gemini at all.</li>
              <li><strong>Sentry</strong> — application error monitoring. Captures error stack traces; we have configured Sentry to NOT capture user-input fields, request bodies, or auth tokens.</li>
              <li><strong>MongoDB Atlas / Emergent-hosted DB</strong> — encrypted-at-rest database for your account information, transactions, and metadata.</li>
              <li><strong>ipinfo.io</strong> — IP-to-coarse-geolocation lookup (state/country only) for trial-abuse prevention and security alerts.</li>
            </ul>
            <p>
              We do <strong>not</strong> sell your data, share it with advertisers, or use it for any
              marketing purpose beyond emailing you the notifications you've subscribed to.
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">5. Your rights (CCPA / general data rights)</h2>
            <p>You have the following rights at any time:</p>
            <ul className="list-disc list-outside ml-6 space-y-2">
              <li><strong>Access:</strong> request a copy of all data we have on you. Use the "Export my data" button in Settings — we'll send you a ZIP archive of your account data, transactions, and uploaded files within 30 days.</li>
              <li><strong>Correction:</strong> edit your account details, transactions, and uploads at any time directly in the app.</li>
              <li><strong>Deletion:</strong> request account deletion. Use the "Delete my account" button in Settings. We honor a 7-day grace period during which you can restore. After 7 days your account and all associated tax data are permanently deleted.</li>
              <li><strong>Opt out of marketing:</strong> we do not run marketing campaigns; however, you can disable renewal reminders in Settings.</li>
              <li><strong>Non-discrimination:</strong> we will not deny service, charge a different price, or provide a different level of service if you exercise any of these rights.</li>
            </ul>
            <p>
              If you are a California resident, the rights above are guaranteed under the California
              Consumer Privacy Act (CCPA). For all other US states, equivalent rights apply where state
              law requires.
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">6. Children's privacy</h2>
            <p>
              This service is for childcare-business operators (adults). It is not directed to
              children under 13, and we do not knowingly collect personal information from children.
              If you believe a child has provided us with personal information, contact us and we
              will delete it.
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">7. Security</h2>
            <p>
              All traffic is encrypted in transit (HTTPS / TLS 1.2+). Passwords are stored hashed
              using bcrypt with per-user salt — we never store or see your plaintext password. Your
              uploaded source files are stored encrypted at rest in Cloudflare R2. Our database is
              encrypted at rest. We enforce HTTP security headers (HSTS, CSP, X-Frame-Options) on
              every response.
            </p>
            <p>
              No system is 100% secure. If we ever experience a data breach that affects you, we
              will notify you by email within 72 hours of confirming the breach, in accordance with
              applicable state-law breach-notification requirements.
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">8. Data retention</h2>
            <p>
              While your account is active, we retain your data as long as you keep using it.
              When you delete your account, we permanently delete your transactions, uploads, CACFP
              records, and account profile within 7 days of confirmation. We may retain certain
              records longer ONLY if required by law (e.g., payment-related records for 7 years per
              IRS guidance).
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">9. Cookies</h2>
            <p>
              We use only first-party cookies necessary for authentication (your login session). We
              do not use third-party advertising cookies, analytics cookies, or cross-site tracking.
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">10. Changes to this policy</h2>
            <p>
              If we make material changes to this policy, we will email all active users at least
              30 days before the changes take effect. The latest version always lives at this URL.
            </p>

            <h2 className="text-lg font-bold text-slate-900 mt-6">11. Contact us</h2>
            <p>
              For privacy questions, data requests, or to report a concern, email{" "}
              <a href="mailto:admin@todayskidzsimplesignin.com" className="text-[#2563EB] font-semibold hover:underline">
                admin@todayskidzsimplesignin.com
              </a>.
            </p>

            <p className="text-xs text-slate-400 italic pt-6 border-t border-slate-200 mt-8">
              This Privacy Policy is provided as a reasonable starting point for a US-based SaaS
              handling tax records. It is not legal advice. We recommend you have it reviewed by an
              attorney before relying on it for compliance with specific state or federal frameworks.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
